/* eslint-disable react/display-name */
import { forwardRef, useImperativeHandle, useState } from 'react';
import QuoteDialog from '@apps/components/CarDetails/CarHighlight/QuoteDialog';
import ComparisonModal from '@apps/containers/ComparisonModal';
import usePostMsgContext from '@apps/containers/WebviewProvider/hooks';
import { Button } from '@mui/material';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import createBasePath from '@stack/helpers/lib/createBasePath';

import { StickyCarDetailCardProps, StickyCarDetailCardRef } from './props';

export const StickyCarDetailCard = forwardRef<
  StickyCarDetailCardRef,
  StickyCarDetailCardProps
>((props, ref) => {
  const { data, location_city, location_state, webView = false } = props;
  // ======================= HOOKS
  const { clickAction } = usePostMsgContext();
  const { basePath } = createBasePath({ webView, type: 'cars' });
  const [isOpen, setIsOpen] = useState(false);
  const [show, setShow] = useState(false);

  useImperativeHandle(ref, () => ({
    setShow: setShow,
    show: show,
  }));

  // ============ EVENTS
  const onOpenGetQuoteDialog = () => {
    setIsOpen(true);
    if (webView) {
      clickAction(basePath, false, {
        title: 'Get Quote',
        params: {
          headerShown: false,
          action: 'headerShown',
        },
      });
    }
  };
  const onClose = () => {
    setIsOpen(false);
    if (webView) {
      clickAction(basePath, false, {
        title: 'Get Quote',
        params: {
          headerShown: true,
          action: 'headerShown',
        },
      });
    }
  };

  // ======================= VARIABLES
  const lowestPrice = data?.lowest_price;
  const highestPrice = data?.highest_price;
  const isTBC =
    parseFloat(lowestPrice || '0') === 0 &&
    parseFloat(highestPrice || '0') === 0;
  // const hasCompare = comparedData && comparedData.length > 0;

  // ======================= EFFECTS

  // ======================= VIEWS
  const renderPrice = () => {
    if (isTBC)
      return (
        <Typography fontWeight={700} fontSize={'0.875rem'} color="#1E242F">
          TBC
        </Typography>
      );
    if (
      lowestPrice &&
      highestPrice &&
      Number(lowestPrice) === Number(highestPrice)
    )
      return (
        <>
          <Typography fontWeight={400} fontSize="0.875rem" color="#1E242F">
            From RM
          </Typography>
          <Typography fontWeight={700} fontSize={'0.875rem'} color="#1E242F">
            {Math.floor(parseFloat(lowestPrice || '0'))?.toLocaleString()}
          </Typography>
        </>
      );
    return (
      <>
        <Typography fontWeight={400} fontSize="0.875rem" color="#1E242F">
          From RM
        </Typography>
        <Typography fontWeight={700} fontSize={'0.875rem'} color="#1E242F">
          {Math.floor(parseFloat(lowestPrice || '0'))?.toLocaleString()} -{' '}
          {Math.floor(parseFloat(highestPrice || '0'))?.toLocaleString()}
        </Typography>
      </>
    );
  };
  return (
    <Stack>
      <ComparisonModal
        disablePathChecing
        sx={{
          position: 'absolute',
          bottom: { xs: 105, lg: 0 },
          transition: 'transform 0.3s ease',
          zIndex: 100,
          transform: {
            xs: show ? 'translateY(0)' : 'translateY(105px)',
            lg: 'unset',
          },
        }}
      />
      <Card
        sx={{
          zIndex: 101,
          bottom: 0,
          left: 0,
          position: 'absolute',
          borderRadius: 0,
          padding: '12px 20px',
          width: '100%',
          display: { xs: 'flex', lg: 'none' },
          flexDirection: 'column',
          gap: 3,
          transition: 'transform 0.3s ease',
          overflow: 'visible',
          transform: show ? 'translateY(0)' : 'translateY(100%)',
        }}
      >
        <QuoteDialog
          isOpen={isOpen}
          onClose={onClose}
          data={{
            carId: data?.new_car_id,
            carImage: data?.car_model_image_full,
            carName: data?.new_car_title,
            carBrandImage: data?.car_brand_image,
            lowestPrice: lowestPrice,
            highestPrice: highestPrice,
          }}
          location_state={location_state}
          location_city={location_city}
          webView={webView}
        />
        <Stack direction="row" sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography
            fontWeight={700}
            fontSize={'1.25rem'}
            sx={{
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {data?.new_car_title}
          </Typography>
          <Stack direction="row" sx={{ marginLeft: 'auto', gap: 1 }}>
            {renderPrice()}
          </Stack>
        </Stack>
        <Button
          sx={{
            borderRadius: 0,
            fontWeight: 700,
            boxShadow: 'none',
          }}
          fullWidth
          color={'secondary'}
          variant="contained"
          onClick={onOpenGetQuoteDialog}
        >
          Get Quote
        </Button>
      </Card>
    </Stack>
  );
});

export default StickyCarDetailCard;
